/* eslint-disable import/export */
import { flow, pipe } from 'fp-ts/function';
import * as monoid from 'fp-ts/Monoid';
import * as option from 'fp-ts/Option';
import * as record from 'fp-ts/Record';
import * as t from 'io-ts';
import { formatValidationErrors } from 'io-ts-reporters';
import * as iotsTypes from 'io-ts-types';
import * as adt from './adt.js';
import * as either from './either.js';
import { fromThrown } from './invariant.js';
import * as string from './string.js';
export * as decoder from './decoder.js';
export { Type, UnionType, any, appendContext, array, boolean, brand, exact, failure, intersection, keyof, literal, never, null as null, number, partial, record, refinement, strict, string, success, tuple, type, undefined, union, unknown, Int, } from 'io-ts';
export * from 'io-ts/Reporter';
export { BooleanFromString, BooleanFromNumber, DateFromISOString, Json, JsonFromString, optionFromNullable, withFallback, withMessage, withValidate, NumberFromString, IntFromString, option as Option, } from 'io-ts-types';
export { formatValidationErrors } from 'io-ts-reporters';
export const nonBlankString = t.brand(t.string, (s) => string.isNotBlank(s), 'NonBlankString');
export const URL = t.brand(t.string, (s) => {
    try {
        new global.URL(s);
        return true;
    }
    catch {
        return false;
    }
}, 'URL');
// -------------------------------------------------------------------------------------------------
/**
 * Parse a value, returning either an array of error messages or the parsed value.
 */
export const parseEither = (decoder) => flow(decoder.decode, either.mapLeft(formatValidationErrors));
/**
 * Parse a value, returning an optional value.
 */
export const parseOption = (decoder) => flow(decoder.decode, option.fromEither);
/**
 * Parse a value or fail by throwing a TypeError.
 */
export const parseSync = (decoder) => flow(parseEither(decoder), either.mapLeft(monoid.concatAll(string.semicolonSeparated)), either.getOrElseW((err) => {
    throw new TypeError(err);
}));
// -------------------------------------------------------------------------------------------------
const prefixedString = (prefix) => new t.Type('Prefixed', (i) => typeof i === 'string' && i.startsWith(prefix), (i, context) => typeof i === 'string' && i.startsWith(prefix)
    ? t.success(i.substring(prefix.length))
    : t.failure(i, context, `Invalid input`), (s) => `${prefix}${s}`);
export const rangedLengthString = ({ min = 0, max = Number.MAX_SAFE_INTEGER, }) => new t.Type('rangedLength', (i) => typeof i === 'string' && i.length <= max && i.length >= min, (i, context) => typeof i === 'string' && i.length <= max && i.length >= min
    ? t.success(i)
    : t.failure(i, context, `String is not in the  length of ${min} to ${max}`), (s) => s);
export const maxLengthString = (max) => rangedLengthString({ max });
export const minLengthString = (min) => rangedLengthString({ min });
export const rangedInteger = ({ min = Number.MIN_SAFE_INTEGER, max = Number.MAX_SAFE_INTEGER, }) => new t.Type('rangedInteger', (i) => t.Int.is(i) && i <= max && i >= min, (i, context) => pipe(t.Int.validate(i, context), either.chain((n) => n <= max && n >= min
    ? t.success(n)
    : t.failure(n, context, `ranged integer not in range ${min} to ${max}`))), (s) => s);
/**
 * Transform a string-based codec by prepending the encoded values with the name of the codec.
 * Useful for discriminating between encoded values of different codecs.
 */
export const prefixed = (codec) => prefixedString(`${codec.name}-`).pipe(codec);
export const tagged = (tag, codec) => codec == null
    ? t.strict({ _tag: t.literal(tag) })
    : t.strict({ _tag: t.literal(tag), value: codec });
/**
 * Creates a branded value from a literal using a type assertion.
 * Caution: The input is not validated; use this function only when it is guaranteed that the
 * input represents a valid value.
 */
export function brandFromLiteral(literal) {
    return literal;
}
/**
 * Drops the brand information from a literal.
 */
export const literalFromBrand = (branded) => branded;
/**
 * Creates an object type with required and optional properties.
 */
export const struct = (required, optional) => t.intersection([t.strict(required), t.exact(t.partial(optional))]);
export const pick = (codec, keys) => {
    const filterProps = record.filterWithIndex((k) => keys.includes(k));
    const makeName = (name, previousKeys) => {
        if (name.startsWith('{') || name.startsWith('('))
            // it's a Union- or Intersection-Name constructed by io-ts
            return undefined; // let io-ts find the new name as well
        const actualKeys = keys
            .filter((k) => previousKeys.includes(k))
            .map((k) => `'${k}'`)
            .join(' | ');
        return `Pick<${name}, ${actualKeys}>`;
    };
    const mapTypes = (types) => types.map((c) => pick(c, keys));
    const fold = adt.foldFromTags('_tag');
    return pipe(codec, fold({
        ExactType: (exact) => t.exact(pick(exact.type, keys)),
        InterfaceType: (codec) => t.type(filterProps(codec.props), makeName(codec.name, record.keys(codec.props))),
        PartialType: (codec) => t.partial(filterProps(codec.props), makeName(codec.name, record.keys(codec.props))),
        IntersectionType: (codec) => t.intersection(mapTypes(codec.types), makeName(codec.name, keys)),
        UnionType: (codec) => t.union(mapTypes(codec.types), makeName(codec.name, keys)),
    }));
};
export const omit = (codec, keys) => {
    const filterProps = record.filterWithIndex((k) => !keys.includes(k));
    const makeName = (name, previousKeys) => {
        if (name.startsWith('{') || name.startsWith('('))
            // it's a Union- or Intersection-Name constructed by io-ts
            return undefined; // let io-ts find the new name as well
        const actualKeys = keys
            .filter((k) => previousKeys.includes(k))
            .map((k) => `'${k}'`)
            .join(' | ');
        return `Omit<${name}, ${actualKeys}>`;
    };
    const mapTypes = (types) => types.map((c) => omit(c, keys));
    const fold = adt.foldFromTags('_tag');
    return pipe(codec, fold({
        ExactType: (exact) => t.exact(omit(exact.type, keys)),
        InterfaceType: (codec) => t.type(filterProps(codec.props), makeName(codec.name, record.keys(codec.props))),
        PartialType: (codec) => t.partial(filterProps(codec.props), makeName(codec.name, record.keys(codec.props))),
        IntersectionType: (codec) => t.intersection(mapTypes(codec.types), makeName(codec.name, keys)),
        UnionType: (codec) => t.union(mapTypes(codec.types), makeName(codec.name, keys)),
    }));
};
export const commaSeparated = (codec) => new t.Type(`CommaSeparatedList<${codec.name}>`, (u) => codec.is(u), (i, context) => pipe(t.string.validate(i, context), either.map((s) => string.split(',')(s)), either.chain((xs) => codec.validate(xs, context))), (as) => pipe(codec.encode(as), monoid.concatAll(string.join(','))));
// FIXME Ugly monkey patching
iotsTypes.date._tag = 'DateType';
export const date = iotsTypes.date;
// FIXME Ugly monkey patching
export const nonEmptyArray = (codec, name) => {
    const c = iotsTypes.nonEmptyArray(codec, name);
    c._tag = 'NonEmptyArrayType';
    c.type = codec;
    return c;
};
export const None = t.strict({
    _tag: t.literal('None'),
});
export const FromJsonString = (codec) => new t.Type('FromJsonString', codec.is, (u, context) => pipe(t.string.validate(u, context), either.chainW((value) => either.tryCatch(() => JSON.parse(value), flow(fromThrown, ({ message }) => [{ value, context, message }]))), either.chain((v) => codec.validate(v, context))), flow(codec.encode, JSON.stringify));
export const Nat = iotsTypes.withMessage(t.brand(t.number, (n) => Number.isInteger(n) && n >= 0, 'Nat'), () => 'Expected a natural number');
/**
 * Parse a Nat using parseInt.
 */
export const NatFromString = new t.Type('NatFromString', (u) => Nat.is(u), (u, c) => pipe(t.string.validate(u, c), either.chain((s) => iotsTypes.NumberFromString.validate(s, c)), either.chain((n) => Nat.validate(n, c))), String);
//# sourceMappingURL=iots.js.map